import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Eye, Target } from 'lucide-react';
import Seo from '../Seo';

const focusAreas = [
  { text: 'Value addition to client service' },
  { text: 'Assistance of seasoned IT professionals' },
  { text: 'Consultants on demand' },
  { text: 'Extraordinary quality at ordinary cost' },
  { text: 'Conducive environment for staff' }
];

const trustedBrands = [
  { name: 'Award 1', logo: '/award-logos/1.webp' },
  { name: 'Award 2', logo: '/award-logos/2.webp' },
  { name: 'Award 3', logo: '/award-logos/3.webp' },
  { name: 'Award 4', logo: '/award-logos/4.webp' },
  { name: 'Award 5', logo: '/award-logos/5.webp' },
  { name: 'Award 6', logo: '/award-logos/6.webp' },
  { name: 'Award 7', logo: '/award-logos/7.webp' }
];

const CompanyOverview = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title="Company Overview"
        description="Libsys Inc company overview — our mission, vision, values, and strategic focus on delivering enterprise IT services and staffing with quality and accountability."
        path="/about-us/company-overview"
      />
      {/* Hero Section with Breadcrumbs */}
      <section className="relative pt-8 pb-12 bg-slate-900 overflow-hidden">
        <div 
          className="absolute inset-x-0 bottom-0 top-1/2 bg-cover bg-center opacity-5 mix-blend-overlay" 
          style={{ backgroundImage: 'url("/dt_hero.png")' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 to-primary" />
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center lg:justify-start gap-2 text-slate-400 text-[12px] font-black uppercase tracking-[0.3em] mb-8"
          >
            <span className="hover:text-white transition-colors cursor-pointer">HOME</span>
            <span className="opacity-40">/</span>
            <span className="text-white">COMPANY OVERVIEW</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl lg:text-6xl font-black text-white leading-tight mb-8"
          >
            Company Overview
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-300 text-lg lg:text-xl font-medium max-w-2xl leading-relaxed"
          >
            Libsys builds technology, staffing, and transformation solutions with a strong focus on delivery quality,
            practical execution, and long-term business value.
          </motion.p>
        </div>
      </section>

      {/* Message From CEO */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="space-y-12">
            <h2 className="text-4xl lg:text-[48px] font-black text-primary leading-tight">Message From CEO</h2>
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
              <div className="space-y-8">
                <p className="text-slate-600 text-[18px] lg:text-[20px] font-medium leading-relaxed italic border-l-8 border-secondary pl-8 bg-slate-50 py-8 pr-8 rounded-r-2xl">
                  "Libsys gives strong credence to clarity of vision and precision of decision making in every stage of growth. 
                  We honor the spirit of diversified America by integrating the best talent, ideas, and work ethics from across the globe."
                </p>
                <div className="space-y-6 text-slate-600 text-[16px] font-medium leading-relaxed">
                  <p>
                    Our leadership maintains a pro-growth approach for employees, clients, associates, and shareholders alike. 
                    We are proud to serve with seasoned expertise in contract staffing, permanent placement, IT project solutions, 
                    vendor management, and workforce training.
                  </p>
                  <p>
                    On behalf of the entire Libsys team, we extend heartfelt gratitude to our customers, vendors, employees, 
                    and well-wishers for your continued support and guidance.
                  </p>
                </div>
                <div className="pt-8 flex items-center gap-6">
                  <div className="w-16 h-[2px] bg-secondary" />
                  <div>
                    <p className="text-primary text-[18px] font-black">Siva Yarramsetty</p>
                    <p className="text-secondary text-[13px] font-black uppercase tracking-[0.25em]">CEO, Libsys Inc.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Redesigned Mission & Vision Statement - More Compact */}
      <section className="bg-slate-50 border-y border-slate-200 overflow-hidden">
        <div className="flex flex-col lg:flex-row min-h-[450px]">
          {/* Left Side - Title Block */}
          <div className="lg:w-[40%] bg-primary p-12 lg:p-16 flex flex-col justify-center text-white relative">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <h2 className="text-3xl lg:text-4xl font-black leading-tight mb-6">
                Our mission and vision statement
              </h2>
              <div className="w-16 h-1.5 bg-secondary mb-6" />
              <p className="text-slate-300 text-base font-medium leading-relaxed opacity-80">
                Defining our purpose, goals, and the future position of our technology partnership.
              </p>
            </motion.div>
          </div>

          {/* Right Side - Iconic Cards */}
          <div className="lg:w-[60%] p-8 lg:p-12 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 bg-slate-50 flex items-center">
            {/* Vision Card */}
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative bg-white pt-12 pb-8 px-6 lg:px-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-500 group"
            >
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 bg-primary rounded-full flex items-center justify-center border-4 border-slate-50 shadow-md group-hover:scale-110 transition-transform duration-500">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <div className="text-center mb-4 mt-2">
                <h3 className="text-primary text-[10px] font-black uppercase tracking-[0.2em] bg-primary/5 py-1.5 inline-block px-5 rounded-full border border-primary/10">Vision</h3>
              </div>
              <div className="space-y-3 text-slate-600 text-[14px] font-medium leading-relaxed text-center">
                <p>
                  Our principle is to become principal in profitability, scalability, and sustainability for global clients.
                </p>
                <p>
                  We unite innovation, people, and process through the potential of technology to nurture trust.
                </p>
              </div>
            </motion.article>

            {/* Mission Card */}
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative bg-white pt-12 pb-8 px-6 lg:px-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-500 group"
            >
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 bg-primary rounded-full flex items-center justify-center border-4 border-slate-50 shadow-md group-hover:scale-110 transition-transform duration-500">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div className="text-center mb-4 mt-2">
                <h3 className="text-primary text-[10px] font-black uppercase tracking-[0.2em] bg-primary/5 py-1.5 inline-block px-5 rounded-full border border-primary/10">Mission</h3>
              </div>
              <div className="space-y-4 text-slate-600 font-medium leading-relaxed">
                <p className="text-[13.5px] text-center">
                  Libsys stands tall on integrity, ethics, and respect to deliver extraordinary excellence.
                </p>
                <div className="space-y-2">
                  {focusAreas.map((item) => (
                    <div key={item.text} className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                      <p className="text-[12.5px] font-bold text-slate-700">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.article>
          </div>
        </div>

        {/* Corporate Anchor Image at Bottom */}
        <div className="relative h-[400px] lg:h-[600px] overflow-hidden">
          <img
            src="/award-logos/collabb.png"
            alt="Corporate Collaboration"
            className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000 transform hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent pointer-events-none" />
        </div>
      </section>

      {/* Trust Scroller (Marquee) - Positioned below redesign */}
      <section className="py-14 bg-white border-b border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 mb-12 text-center">
          <p className="text-strategic-tagline text-[11px] font-black uppercase tracking-[0.3em] mb-4">Trusted Worldwide</p>
          <h2 className="text-3xl lg:text-4xl font-black text-primary">Powering Industry Leaders</h2>
        </div>
        <div className="relative flex overflow-x-hidden">
          <div className="flex animate-marquee whitespace-nowrap py-8">
            {[...trustedBrands, ...trustedBrands].map((brand, index) => (
              <div
                key={`${brand.name}-${index}`}
                className="mx-10 relative rounded-xl p-[1px] bg-gradient-to-br from-slate-200 via-slate-300 to-slate-200 hover:from-secondary/60 hover:via-primary/50 hover:to-secondary/60 shadow-sm hover:shadow-[0_18px_36px_-20px_rgba(26,58,107,0.45)] hover:-translate-y-2 transition-all duration-500 group flex-shrink-0"
              >
                <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.18),transparent_65%)] transition-opacity duration-500 pointer-events-none" />
                <div className="bg-white rounded-[11px] p-6 flex items-center justify-center h-40 w-72">
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="max-w-[85%] max-h-[85%] object-contain opacity-90 group-hover:opacity-100 transition-all duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyOverview;
